import React, { useState } from 'react';
import { Icon, Modal } from 'Components';
import LMSButton from 'LearningManager/components/Button';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import UserCountError from 'LearningManager/components/UserCountError';
import useAssignmentsUserCount, {
	ResolveAssignmentUsers
} from 'LearningManager/hooks/useAssignmentsUserCount';
import styles from './CourseUnassignModal.module.scss';

const plural = (count: number, noun: string) =>
	`${count} ${noun}${count === 1 ? '' : 's'}`;

export interface CourseUnassignAssignmentOption {
	id: number;
	label: string;
}

interface CourseUnassignModalProps {
	visible: boolean;
	options: CourseUnassignAssignmentOption[];
	resolveAssignmentUsers: ResolveAssignmentUsers;
	onCancel: () => void;
	onConfirm: (assignmentIds: number[]) => void;
}

const CourseUnassignModal: React.FC<CourseUnassignModalProps> = ({
	visible,
	options,
	resolveAssignmentUsers,
	onCancel,
	onConfirm
}) => {
	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const hasSelection = selectedIds.length > 0;
	const {
		count: userCount,
		failedCount,
		retry
	} = useAssignmentsUserCount(
		selectedIds,
		resolveAssignmentUsers,
		visible
	);

	const handleCancel = () => {
		setSelectedIds([]);
		onCancel();
	};

	const handleConfirm = () => {
		onConfirm(selectedIds);
		setSelectedIds([]);
	};

	return (
		<Modal
			visible={visible}
			title="Unassign courses?"
			onCancel={handleCancel}
			size="sm"
			destroyOnClose
			className={styles.modal}
			footer={
				<div className={styles.footer}>
					<LMSButton
						size="small"
						variant="borderless"
						type="default"
						onClick={handleCancel}
					>
						Cancel
					</LMSButton>
					<LMSButton
						type="primary"
						size="small"
						disabled={!hasSelection || !userCount}
						onClick={handleConfirm}
					>
						Unassign
					</LMSButton>
				</div>
			}
		>
			<div className={styles.body}>
				<p className={styles.text}>
					Select the assignment(s) you want to unassign this course
					from.
				</p>
				<div className={styles.field}>
					<div className={styles.field__label}>Assignment</div>
					<MultiSelectDropdown
						options={options}
						placeholder="Select assignment"
						defaultSelectedIds={[]}
						onChange={setSelectedIds}
						allLabel="All Assignments"
						showSelectedLabels
						className={styles.dropdown}
					/>
				</div>

				{hasSelection && (
					<div className={styles.summary}>
						<p className={styles.text}>
							Unassigning this courses will:
						</p>
						{failedCount > 0 ? (
							<UserCountError
								failedCount={failedCount}
								noun="learner"
								onRetry={retry}
							/>
						) : (
							<ul className={styles.list}>
								<li>
									Affect{' '}
									{userCount === undefined ? (
										<Icon
											type="loading"
											aria-label="Counting users"
										/>
									) : (
										plural(userCount, 'learner')
									)}{' '}
									total
								</li>
							</ul>
						)}
						<div className={styles.warning}>
							<Icon type="warningFilledIcon" role="img" />
							This action cannot be undone. All progress data will
							be lost.
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CourseUnassignModal;
